'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Minus,
  Square,
  TrendingUp,
  MousePointer,
  Trash2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Move,
} from 'lucide-react';

interface DrawingCanvasProps {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
}

interface Point {
  x: number;
  y: number;
}

interface DrawnShape {
  id: string;
  type: string;
  start: Point;
  end: Point;
  color: string;
}

// Mock candlestick data for the chart
const candleData = [
  { o: 128.2, h: 131.4, l: 127.1, c: 130.8, v: 42000 },
  { o: 130.8, h: 133.2, l: 129.5, c: 132.1, v: 38000 },
  { o: 132.1, h: 134.8, l: 130.2, c: 131.4, v: 55000 },
  { o: 131.4, h: 132.0, l: 128.8, c: 129.2, v: 31000 },
  { o: 129.2, h: 130.5, l: 126.4, c: 127.8, v: 48000 },
  { o: 127.8, h: 129.2, l: 125.1, c: 128.6, v: 62000 },
  { o: 128.6, h: 132.4, l: 128.0, c: 131.9, v: 71000 },
  { o: 131.9, h: 135.6, l: 131.2, c: 134.8, v: 84000 },
  { o: 134.8, h: 137.2, l: 133.4, c: 136.4, v: 91000 },
  { o: 136.4, h: 138.8, l: 134.2, c: 135.1, v: 67000 },
  { o: 135.1, h: 136.0, l: 131.8, c: 132.4, v: 53000 },
  { o: 132.4, h: 133.2, l: 129.6, c: 130.8, v: 44000 },
  { o: 130.8, h: 134.6, l: 130.2, c: 133.9, v: 78000 },
  { o: 133.9, h: 137.4, l: 133.1, c: 136.8, v: 88000 },
  { o: 136.8, h: 140.2, l: 136.0, c: 139.4, v: 95000 },
  { o: 139.4, h: 141.8, l: 137.6, c: 138.2, v: 72000 },
  { o: 138.2, h: 139.4, l: 135.8, c: 136.6, v: 58000 },
  { o: 136.6, h: 140.8, l: 136.0, c: 140.2, v: 86000 },
  { o: 140.2, h: 143.6, l: 139.4, c: 142.8, v: 102000 },
  { o: 142.8, h: 145.2, l: 141.6, c: 144.4, v: 118000 },
  { o: 144.4, h: 146.8, l: 142.2, c: 143.6, v: 94000 },
  { o: 143.6, h: 144.2, l: 140.4, c: 141.8, v: 69000 },
  { o: 141.8, h: 145.6, l: 141.2, c: 144.8, v: 81000 },
  { o: 144.8, h: 148.4, l: 144.2, c: 147.6, v: 110000 },
  { o: 147.6, h: 150.2, l: 146.8, c: 149.4, v: 125000 },
  { o: 149.4, h: 152.8, l: 148.6, c: 151.2, v: 138000 },
  { o: 151.2, h: 153.4, l: 149.8, c: 150.6, v: 99000 },
  { o: 150.6, h: 151.8, l: 147.4, c: 148.8, v: 76000 },
  { o: 148.8, h: 152.6, l: 148.2, c: 151.8, v: 88000 },
  { o: 151.8, h: 155.4, l: 151.2, c: 154.6, v: 142000 },
];

const labels = [
  'Jul 1','Jul 2','Jul 3','Jul 4','Jul 7','Jul 8','Jul 9','Jul 10','Jul 11','Jul 14',
  'Jul 15','Jul 16','Jul 17','Jul 18','Jul 21','Jul 22','Jul 23','Jul 24','Jul 25','Jul 28',
  'Jul 29','Jul 30','Jul 31','Aug 1','Aug 4','Aug 5','Aug 6','Aug 7','Aug 8','Aug 11',
];

const tools = [
  { id: 'select', icon: <MousePointer size={15} />, label: 'Select / Move' },
  { id: 'trendline', icon: <TrendingUp size={15} />, label: 'Trend Line' },
  { id: 'horizontal', icon: <Minus size={15} />, label: 'Support / Resistance' },
  { id: 'rectangle', icon: <Square size={15} />, label: 'Pattern Rectangle' },
  { id: 'pan', icon: <Move size={15} />, label: 'Pan Chart' },
];

const toolColors: Record<string, string> = {
  trendline: '#00D4AA',
  horizontal: '#F59E0B',
  rectangle: '#6366F1',
};

export default function DrawingCanvas({ selectedTool, setSelectedTool, canvasRef: externalCanvasRef }: DrawingCanvasProps) {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = (externalCanvasRef as React.RefObject<HTMLCanvasElement>) || internalCanvasRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const [shapes, setShapes] = useState<DrawnShape[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [startPt, setStartPt] = useState<Point>({ x: 0, y: 0 });
  const [currentPt, setCurrentPt] = useState<Point>({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 420 });
  const [hoveredShape, setHoveredShape] = useState<string | null>(null);

  const PADDING = { top: 20, right: 60, bottom: 50, left: 16 };

  const getMinMax = () => {
    let min = Infinity, max = -Infinity;
    candleData.forEach((c) => {
      if (c.l < min) min = c.l;
      if (c.h > max) max = c.h;
    });
    return { min: min - 2, max: max + 2 };
  };

  const priceToY = useCallback((price: number, h: number) => {
    const { min, max } = getMinMax();
    const chartH = h - PADDING.top - PADDING.bottom;
    return PADDING.top + chartH - ((price - min) / (max - min)) * chartH;
  }, [canvasSize]);

  const indexToX = useCallback((i: number, w: number) => {
    const chartW = w - PADDING.left - PADDING.right;
    const candleW = chartW / candleData.length;
    return PADDING.left + i * candleW + candleW / 2;
  }, [canvasSize]);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { w, h } = canvasSize;
    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, w, h);

    const { min, max } = getMinMax();
    const chartW = w - PADDING.left - PADDING.right;
    const candleW = (chartW / candleData.length) * 0.6;

    // Grid lines
    const priceSteps = 6;
    for (let i = 0; i <= priceSteps; i++) {
      const price = min + ((max - min) / priceSteps) * i;
      const y = priceToY(price, h);
      ctx.beginPath();
      ctx.strokeStyle = '#1E2D45';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.moveTo(PADDING.left, y);
      ctx.lineTo(w - PADDING.right, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Price labels
      ctx.fillStyle = '#6B7A99';
      ctx.font = '10px IBM Plex Mono, monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`$${price.toFixed(1)}`, w - 4, y + 3);
    }

    // Date labels
    ctx.fillStyle = '#6B7A99';
    ctx.font = '10px Plus Jakarta Sans, sans-serif';
    ctx.textAlign = 'center';
    const labelStep = Math.ceil(candleData.length / 8);
    candleData.forEach((_, i) => {
      if (i % labelStep === 0) {
        const x = indexToX(i, w);
        ctx.fillText(labels[i] || '', x, h - 8);
      }
    });

    // Candlesticks
    candleData.forEach((candle, i) => {
      const x = indexToX(i, w);
      const openY = priceToY(candle.o, h);
      const closeY = priceToY(candle.c, h);
      const highY = priceToY(candle.h, h);
      const lowY = priceToY(candle.l, h);
      const isBull = candle.c >= candle.o;
      const color = isBull ? '#00D4AA' : '#F43F5E';

      // Wick
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.moveTo(x, highY);
      ctx.lineTo(x, lowY);
      ctx.stroke();

      // Body
      const bodyTop = Math.min(openY, closeY);
      const bodyH = Math.max(Math.abs(closeY - openY), 2);
      ctx.fillStyle = isBull ? color : color;
      ctx.globalAlpha = isBull ? 0.85 : 0.75;
      ctx.fillRect(x - candleW / 2, bodyTop, candleW, bodyH);
      ctx.globalAlpha = 1;
    });

    // Volume bars at bottom
    const volMax = Math.max(...candleData.map((c) => c.v));
    const volH = 30;
    const volTop = h - PADDING.bottom - volH;
    candleData.forEach((candle, i) => {
      const x = indexToX(i, w);
      const barH = (candle.v / volMax) * volH;
      const isBull = candle.c >= candle.o;
      ctx.fillStyle = isBull ? '#00D4AA' : '#F43F5E';
      ctx.globalAlpha = 0.25;
      ctx.fillRect(x - candleW / 2, volTop + volH - barH, candleW, barH);
      ctx.globalAlpha = 1;
    });

    // Draw existing shapes
    shapes.forEach((shape) => {
      const isHovered = hoveredShape === shape.id;
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = isHovered ? 2.5 : 1.8;
      ctx.globalAlpha = isHovered ? 1 : 0.85;

      if (shape.type === 'trendline') {
        ctx.beginPath();
        ctx.setLineDash([]);
        ctx.moveTo(shape.start.x, shape.start.y);
        ctx.lineTo(shape.end.x, shape.end.y);
        ctx.stroke();
        // Endpoint dots
        [shape.start, shape.end].forEach((pt) => {
          ctx.beginPath();
          ctx.fillStyle = shape.color;
          ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2);
          ctx.fill();
        });
      } else if (shape.type === 'horizontal') {
        ctx.beginPath();
        ctx.setLineDash([6, 4]);
        ctx.moveTo(PADDING.left, shape.start.y);
        ctx.lineTo(w - PADDING.right, shape.start.y);
        ctx.stroke();
        ctx.setLineDash([]);
        // Price label
        ctx.fillStyle = shape.color;
        ctx.font = '10px IBM Plex Mono, monospace';
        ctx.textAlign = 'right';
        const price = min + ((max - min) * (1 - (shape.start.y - PADDING.top) / (h - PADDING.top - PADDING.bottom)));
        ctx.fillText(`$${price.toFixed(2)}`, w - 4, shape.start.y - 3);
      } else if (shape.type === 'rectangle') {
        ctx.beginPath();
        ctx.setLineDash([4, 3]);
        const rx = Math.min(shape.start.x, shape.end.x);
        const ry = Math.min(shape.start.y, shape.end.y);
        const rw = Math.abs(shape.end.x - shape.start.x);
        const rh = Math.abs(shape.end.y - shape.start.y);
        ctx.strokeRect(rx, ry, rw, rh);
        ctx.fillStyle = shape.color;
        ctx.globalAlpha = 0.06;
        ctx.fillRect(rx, ry, rw, rh);
        ctx.setLineDash([]);
      }
      ctx.globalAlpha = 1;
    });

    // Draw active shape
    if (drawing && selectedTool !== 'select' && selectedTool !== 'pan') {
      const color = toolColors[selectedTool] || '#00D4AA';
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.8;
      ctx.globalAlpha = 0.7;

      if (selectedTool === 'trendline') {
        ctx.beginPath();
        ctx.setLineDash([]);
        ctx.moveTo(startPt.x, startPt.y);
        ctx.lineTo(currentPt.x, currentPt.y);
        ctx.stroke();
      } else if (selectedTool === 'horizontal') {
        ctx.beginPath();
        ctx.setLineDash([6, 4]);
        ctx.moveTo(PADDING.left, startPt.y);
        ctx.lineTo(w - PADDING.right, startPt.y);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (selectedTool === 'rectangle') {
        const rx = Math.min(startPt.x, currentPt.x);
        const ry = Math.min(startPt.y, currentPt.y);
        const rw = Math.abs(currentPt.x - startPt.x);
        const rh = Math.abs(currentPt.y - startPt.y);
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(rx, ry, rw, rh);
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.06;
        ctx.fillRect(rx, ry, rw, rh);
        ctx.setLineDash([]);
      }
      ctx.globalAlpha = 1;
    }
  }, [canvasSize, shapes, drawing, startPt, currentPt, selectedTool, hoveredShape, priceToY, indexToX]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ w: rect.width, h: 420 });
      }
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    drawChart();
  }, [drawChart]);

  const getRelativePoint = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (selectedTool === 'select' || selectedTool === 'pan') return;
    const pt = getRelativePoint(e);
    setDrawing(true);
    setStartPt(pt);
    setCurrentPt(pt);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pt = getRelativePoint(e);
    if (drawing) setCurrentPt(pt);
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    const pt = getRelativePoint(e);
    const newShape: DrawnShape = {
      id: `shape-${Date.now()}-${shapes.length}`,
      type: selectedTool,
      start: startPt,
      end: pt,
      color: toolColors[selectedTool] || '#00D4AA',
    };
    setShapes((prev) => [...prev, newShape]);
    setDrawing(false);
  };

  const getCursorClass = () => {
    if (selectedTool === 'select') return 'cursor-default';
    if (selectedTool === 'pan') return 'cursor-grab';
    return 'cursor-crosshair';
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden card-glow-primary">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-1">
          {/* Symbol selector */}
          <div className="flex items-center gap-2 mr-3">
            <select className="bg-muted border border-border rounded-lg px-3 py-1.5 text-sm text-foreground outline-none focus:border-primary/60 font-mono-nums">
              {['NVDA', 'AAPL', 'TSLA', 'SPY', 'QQQ', 'META', 'MSFT', 'AMZN'].map((sym) => (
                <option key={`sym-${sym}`} value={sym}>{sym}</option>
              ))}
            </select>
            <select className="bg-muted border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary/60">
              {['1m', '5m', '15m', '1H', '4H', '1D'].map((tf) => (
                <option key={`tf-canvas-${tf}`} value={tf}>{tf}</option>
              ))}
            </select>
          </div>

          {/* Drawing tools */}
          <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-1">
            {tools.map((tool) => (
              <button
                key={`tool-${tool.id}`}
                onClick={() => setSelectedTool(tool.id)}
                title={tool.label}
                className={`p-2 rounded-md transition-all duration-150 ${
                  selectedTool === tool.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tool.icon}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            title="Zoom in"
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          >
            <ZoomIn size={14} />
          </button>
          <button
            title="Zoom out"
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          >
            <ZoomOut size={14} />
          </button>
          <div className="w-px h-4 bg-border mx-1" />
          <button
            title="Undo last drawing"
            onClick={() => setShapes((prev) => prev.slice(0, -1))}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          >
            <RotateCcw size={14} />
          </button>
          <button
            title="Clear all drawings"
            onClick={() => setShapes([])}
            className="p-2 rounded-md text-muted-foreground hover:text-negative hover:bg-negative-subtle transition-all"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="relative w-full">
        <canvas
          ref={canvasRef}
          width={canvasSize.w}
          height={canvasSize.h}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => setDrawing(false)}
          className={`w-full block ${getCursorClass()}`}
        />

        {/* Empty drawings hint */}
        {shapes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-card/80 border border-border/60 rounded-xl px-5 py-3 backdrop-blur-sm text-center">
              <p className="text-sm font-500 text-foreground">Draw your strategy pattern</p>
              <p className="text-xs text-muted-foreground mt-1">
                Select a tool above and mark your entry signal on the chart
              </p>
            </div>
          </div>
        )}

        {/* Shape count badge */}
        {shapes.length > 0 && (
          <div className="absolute top-3 right-3 bg-primary/10 border border-primary/20 rounded-lg px-2.5 py-1.5">
            <p className="text-xs font-500 text-primary">{shapes.length} drawing{shapes.length > 1 ? 's' : ''}</p>
          </div>
        )}
      </div>

      {/* Drawn shapes list */}
      {shapes.length > 0 && (
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs font-500 text-muted-foreground mb-2">Strategy Pattern Elements</p>
          <div className="flex flex-wrap gap-2">
            {shapes.map((shape, i) => (
              <div
                key={shape.id}
                className="flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-2.5 py-1.5 group"
                onMouseEnter={() => setHoveredShape(shape.id)}
                onMouseLeave={() => setHoveredShape(null)}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: shape.color }}
                />
                <span className="text-xs text-foreground capitalize">
                  {shape.type === 'horizontal' ? 'S/R Line' : shape.type === 'trendline' ? 'Trend Line' : 'Rectangle'} {i + 1}
                </span>
                <button
                  onClick={() => setShapes((prev) => prev.filter((s) => s.id !== shape.id))}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-negative"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}